import React, { useCallback, useEffect, useRef, useState } from "react";

const useNumber = (initialValue: number = 0): [number, React.Dispatch<React.SetStateAction<number>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useBoolean = (initialValue: boolean = false): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useString = (initialValue: string = ""): [string, React.Dispatch<React.SetStateAction<string>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useArray = <T extends any[]>(
  initialValue: T = [] as unknown as T,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useObject = <T extends object>(initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useDate = (initialValue: Date = new Date()): [Date, React.Dispatch<React.SetStateAction<Date>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useFunction = <T extends (...args: any[]) => any>(
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const usePromise = <T extends any>(
  initialValue: Promise<T> = Promise.resolve(null as unknown as T),
): [Promise<T>, React.Dispatch<React.SetStateAction<Promise<T>>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useNull = <T extends any>(
  initialValue: T = null as unknown as T,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useTuple = <T extends any[]>(initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useRecord = <K extends keyof any, T>(
  initialValue: Record<K, T>,
): [Record<K, T>, React.Dispatch<React.SetStateAction<Record<K, T>>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useUnion = <T extends string | number>(initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useIntersection = <A extends object, B extends object>(
  initialValue: A & B,
): [A & B, React.Dispatch<React.SetStateAction<A & B>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

const useNullable = <T>(initialValue: T | null): [T | null, React.Dispatch<React.SetStateAction<T | null>>] => {
  const [value, setValue] = useState(initialValue);
  return [value, setValue];
};

function useError() {
  const [error, setError] = useState<Error | null>(null);
  const throwError = (error: Error) => setError(error);
  return [error, throwError] as const;
}

// useDefault
function useDefault<T>(state: T | null | undefined, defaultValue: T): T {
  return state == null ? defaultValue : state;
}

// useGetSet
function useGetSet<T>(initialValue: T): [() => T, (value: T) => void] {
  const state = useRef(initialValue);
  const getState = () => state.current;
  const setState = (value: T) => {
    state.current = value;
  };

  return [getState, setState];
}

// useLatest
function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

// usePrevious
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// usePreviousDistinct
function usePreviousDistinct<T>(value: T, compareFn: (prev: T, next: T) => boolean): T | undefined {
  const previousRef = useRef<T>();
  const currentRef = useRef(value);

  if (compareFn(currentRef.current, value)) {
    previousRef.current = currentRef.current;
    currentRef.current = value;
  }

  return previousRef.current;
}

// useCounter
function useCounter(initialValue: number = 0): [number, () => void, () => void] {
  const [count, setCount] = useState(initialValue);
  const increment = useCallback(() => setCount((x) => x + 1), []);
  const decrement = useCallback(() => setCount((x) => x - 1), []);

  return [count, increment, decrement];
}

// useList
function useList<T>(initialValue: T[]): [T[], (value: T) => void, (index: number) => void] {
  const [list, setList] = useState(initialValue);
  const push = useCallback((item: T) => {
    setList((l) => [...l, item]);
  }, []);
  const remove = useCallback((index: number) => {
    setList((l) => [...l.slice(0, index), ...l.slice(index + 1)]);
  }, []);

  return [list, push, remove];
}

// useMap
function useMap<K extends string | number, V>(
  initialValue: Record<K, V>,
): [Record<K, V>, (k: K, v: V) => void, (k: K) => void] {
  const [map, setMap] = useState(initialValue);
  const set = useCallback((key: K, value: V) => {
    setMap((m) => ({ ...m, [key]: value }));
  }, []);
  const remove = useCallback((key: K) => {
    setMap((m) => {
      const newMap = { ...m };
      delete newMap[key];
      return newMap;
    });
  }, []);

  return [map, set, remove];
}

// useSet
function useSet<T>(initialSet: Set<T>) {
  const [set, setSet] = useState(initialSet);

  function add(item: T) {
    setSet((prev) => new Set([...prev, item]));
  }

  function remove(item: T) {
    setSet((prev) => new Set(Array.from(prev).filter((i) => i !== item)));
  }

  function clear() {
    setSet(new Set());
  }

  return { set, add, remove, clear };
}

// useQueue
function useQueue<T>(initialQueue: T[] = []) {
  const queueRef = useRef(initialQueue);

  function enqueue(item: T) {
    queueRef.current.push(item);
  }

  function dequeue(): T | undefined {
    return queueRef.current.shift();
  }

  return { enqueue, dequeue };
}

// useStateValidator
function useStateValidator<T>(initialState: T, validator: (state: T) => boolean) {
  const [state, setState] = useState(initialState);
  const isValid = validator(state);

  return [state, setState, isValid] as const;
}

// useStateWithHistory
function useStateWithHistory<T>(initialState: T, capacity = 10) {
  const [state, setState] = useState(initialState);
  const history = useRef([initialState]);

  function set(value: T) {
    history.current.push(value);

    if (history.current.length > capacity) {
      history.current.shift();
    }

    setState(value);
  }

  function goBack() {
    if (history.current.length <= 1) return;

    history.current.pop();

    setState(history.current[history.current.length - 1]);
  }

  return [state, set, { history: history.current, goBack }] as const;
}

// useMultiStateValidator
function useMultiStateValidator<T extends object>(
  initialState: T,
  validator: (state: T) => { [K in keyof T]: boolean },
) {
  const [state, setState] = useState(initialState);
  const isValid = validator(state);

  return [state, setState, isValid] as const;
}

// useMediatedState
function useMediatedState<T>(initialState: T, mediator: (state: T) => T) {
  const [state, setState] = useState(initialState);

  function set(value: T) {
    setState(mediator(value));
  }

  return [state, set] as const;
}

// useFirstMountState
function useFirstMountState() {
  const isFirst = useRef(true);

  if (isFirst.current) {
    isFirst.current = false;
    return true;
  }

  return false;
}

// useRendersCount
function useRendersCount() {
  const renders = useRef(0);

  useEffect(() => {
    renders.current++;
  });

  return renders.current;
}

// createGlobalState
const globalState = new Map();

function createGlobalState<T>(key: string, initialValue: T) {
  if (!globalState.has(key)) {
    globalState.set(key, { value: initialValue, subscribers: new Set<React.Dispatch<React.SetStateAction<T>>>() });
  }

  const state = globalState.get(key)!;

  function setState(value: T | ((prevState: T) => T)) {
    if (typeof value === "function") {
      state.value = (value as (prevState: T) => T)(state.value);
    } else {
      state.value = value;
    }

    state.subscribers.forEach((fn: React.Dispatch<React.SetStateAction<T>>) => fn(state.value));
  }

  function useGlobalState(): [T, typeof setState] {
    const setStateReact = useState(state.value)[1];

    useEffect(() => {
      state.subscribers.add(setStateReact);

      return () => {
        state.subscribers.delete(setStateReact);
      };
    }, []);

    return [state.value, setState];
  }

  return useGlobalState;
}

function useMethods<T extends object, U extends { [K: string]: (...args: unknown[]) => Partial<T> }>(
  methods: U,
  initialState: T,
): [T, { [K in keyof U]: (...args: Parameters<U[K]>) => void }] {
  const [state, setState] = useState<T>(initialState);

  const boundMethods = Object.entries(methods).reduce<{ [K in keyof U]: (...args: unknown[]) => void }>(
    (acc, [name, fn]) => {
      acc[name as keyof U] = (...args) => {
        setState((prevState) => ({ ...prevState, ...fn(...args) }));
      };
      return acc;
    },
    {} as { [K in keyof U]: (...args: unknown[]) => void },
  );

  return [state, boundMethods];
}

export {
  useNumber,
  useBoolean,
  useString,
  useArray,
  useObject,
  useDate,
  useFunction,
  useMap,
  useSet,
  usePromise,
  useNull,
  useTuple,
  useRecord,
  useUnion,
  useIntersection,
  useNullable,
  useError,
  useDefault,
  useGetSet,
  useLatest,
  usePrevious,
  usePreviousDistinct,
  useCounter,
  useList,
  useFirstMountState,
  useRendersCount,
  createGlobalState,
  useMethods,
  useMediatedState,
  useStateValidator,
  useMultiStateValidator,
  useStateWithHistory,
  useQueue,
};
